import { Injectable, EventEmitter } from '@angular/core';
import { Platform } from 'ionic-angular';
import { DELIVERY_TYPE, EVENTS_VALUES, OTP_REGISTRATION_RET_VAL, OTP_RESULT, OTP_SESSION_EXPIRED_KEY_MAP } from '../myria/app-variables';
import { TranslateService } from '@ngx-translate/core';
import { LoggerProvider, LoggerEvent } from '../logger/logger';
import { HttpProvider } from '../../providers/http/http';
import { RouteProvider } from '../../providers/route/route';
import { SegmentProvider } from '../analytics/segment';
import { AnalyticsEventType, SegmentLocationNames } from '../../model/types/analytics.const';
import { LoadingProvider } from '../loading/loading';
import { UtilsProvider } from '../myria/utils-provider';
import { FeatureFlagTypes } from '../../model/types/feature-flag-types';

export interface ChannelSelectorAnalytics {
  flow: string;
  country: string;
}

export const BASE_ORDER = [EVENTS_VALUES.SMS, EVENTS_VALUES.WHATSAPP, EVENTS_VALUES.EMAIL, EVENTS_VALUES.VOICE];

const BE_CHANNEL_TYPE = { SMS: 'sms', WHATSAPP: 'whatsapp' };

export const WHATSAPP_FLOW = { SIGNUP: 'signup', LOGIN: 'login', RECOVERY: 'recovery' };
const WHATSAPP_ELIGIBLE_FLOWS = [WHATSAPP_FLOW.SIGNUP, WHATSAPP_FLOW.LOGIN];

export enum MobileVerifyOutcome {
  OpenOtp = 'OPEN_OTP',
  Blocked = 'BLOCKED',
  TwilioUnavailable = 'TWILIO_UNAVAILABLE',
  MaxAttempts = 'MAX_ATTEMPTS',
  SendBlocked = 'SEND_BLOCKED',
  Invalid = 'INVALID',
  Failed = 'FAILED'
}

export interface MobileVerifyClassification {
  outcome: MobileVerifyOutcome;
  otpResult: number | null;
  value: any;
}

@Injectable()
export class OtpTimerProvider {
  private identifier: string;
  private channelSequence: string[];
  private timerEndtimes: { [channel: string]: number } = {};
  private timerIntervals: { [channel: string]: any } = {};
  private inputFocusTimeout: any;
  private resumeSubscription: any;
  timerFinished: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private translate: TranslateService,
    private http: HttpProvider,
    private routeProvider: RouteProvider,
    private segment: SegmentProvider,
    private loadingProvider: LoadingProvider,
    private platform: Platform) {
  }

  setIdentifier(identifier: string, pageId: number) {
    this.identifier = identifier + '_' + pageId;
  }

  getChannelIdentifier(channel: string) {
    return channel + '_' + this.identifier;
  }

  setChannelSequence(sequence: string[]) {

    sequence.forEach(channel => {
      if (!BASE_ORDER.includes(channel)) {
        LoggerProvider.error(this, LoggerEvent.OtpTimerException, `Invalid channel: ${channel}. Valid channels are: ${BASE_ORDER.join(', ')}`);
      }
    });

    this.channelSequence = sequence;
  }

  checkAndStartTimer(otpTimerResponse, identifier: string, pageType: number) {
    this.setIdentifier(identifier, pageType);

    if (this.isCooldownEnable(otpTimerResponse)) {
      const channel = this.getChannelFromResponse(otpTimerResponse.Value);
      this.startTimer(channel, this.getChannelCooldown(channel, otpTimerResponse.Value));
    }
  }

  startTimer(channel: string, durationSeconds: number) {
    if (this.isRunning(channel)) return;

    const channelIdentifier = this.getChannelIdentifier(channel);

    this.timerEndtimes[channelIdentifier] = Date.now() + durationSeconds * 1000;

    if (this.timerIntervals[channelIdentifier]) {
      clearInterval(this.timerIntervals[channelIdentifier]);
    }

    const updateTime = () => {
      const remaining = this.getRemainingSeconds(channel);
      this.updateClockValue(channel);

      if (remaining <= 0) {
        this.timerFinished.emit(channel);
        clearInterval(this.timerIntervals[channelIdentifier]);
        delete this.timerIntervals[channelIdentifier];
        delete this.timerEndtimes[channelIdentifier];
      }
    };

    updateTime();
    this.timerIntervals[channelIdentifier] = setInterval(updateTime, 1000);
  }

  getRemainingSeconds(channel: string): number {
    const now = Date.now();
    const endtime = this.timerEndtimes[this.getChannelIdentifier(channel)];

    if (!endtime) return 0;
    
    return Math.max(0, Math.floor((endtime - now) / 1000));
  }

  isRunning(channel: string): boolean {
    return this.getRemainingSeconds(channel) > 0;
  }

  updateClockValue(channel: string) {
    const clock = document.getElementById(this.getChannelClockDivId(channel));
    const formatted = this.format(this.timerEndtimes[this.getChannelIdentifier(channel)] || 0);
    const rs = this.getRemainingSeconds(channel);

    if (rs > 0) {
      if (clock) clock.innerHTML = `${formatted}`;
    } else {
      if (clock) clock.innerHTML = '';
    }
  }

  getButtonLabel(channel: string, fallbackLabel: string = ""): string {
    const rs = this.getRemainingSeconds(channel);

    switch (channel) {
      case EVENTS_VALUES.SMS: return rs > 0 ? this.translate.instant('global.mFAContainer.mFAResendSMS') : this.translate.instant('global.mFAContainer.mFASendViaSMS');
      case EVENTS_VALUES.EMAIL: return rs > 0 ? this.translate.instant('global.mFAContainer.mFAResendEmail') : this.translate.instant('global.mFAContainer.mFASendViaEmail');
      case EVENTS_VALUES.VOICE: return this.translate.instant('global.mFAContainer.mFAPhoneCall');
      case EVENTS_VALUES.WHATSAPP: return this.translate.instant('page.otp.verificationHelp.resendWhatsapp');
      default: return fallbackLabel ? fallbackLabel : 'Send code';
    }
  }

  format(endtime: number) {
    let message;
    const now = Date.now();
    const total = Math.max(0, endtime - now);
    const totalSeconds = Math.floor(total / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) message = `(${hours}h:${minutes}m:${seconds}s)`;
    else if (minutes > 0) message = `(${minutes}m:${seconds}s)`;
    else message = message = `(0:${seconds}s)`;

    return message;
  }

  getChannelClockDivId(channel: string) {
    switch(channel) {
      case EVENTS_VALUES.SMS: return 'smsclockdiv';
      case EVENTS_VALUES.EMAIL: return 'emailclockdiv';
      case EVENTS_VALUES.VOICE: return 'phoneclockdiv';
      case EVENTS_VALUES.WHATSAPP: return 'whatsappclockdiv';
    }
  }

  getChannelSequence(): string[] {
  
    let active = [];
    let inactive = [];
    const totalButtonsLength = 3; //primary, secondary, tertiary.

    const baseOrderIndex = BASE_ORDER.reduce((acc, channel, index) => {
      acc[channel] = index;
      return acc;
    }, {});

    this.channelSequence.forEach(channel => {
      if (this.isRunning(channel)) {
        active.push(channel);
      } else {
        inactive.push(channel);
      }
    });
  
    active = active.sort((a, b) => baseOrderIndex[a] - baseOrderIndex[b]);
    inactive = inactive.sort((a, b) => baseOrderIndex[a] - baseOrderIndex[b]);

    const orderedSequence = [...active, ...inactive];

    while (orderedSequence.length < totalButtonsLength) {
      orderedSequence.push(undefined);
    }

    return orderedSequence.slice(0, totalButtonsLength);
  }

  getChannelConfigs(actionFn: (channel: string) => void) {
    let primaryConfigs = {}, secondaryConfigs = {}, tertiaryConfigs = {};
    const [primaryChannel, secondaryChannel, tertiaryChannel]  = this.getChannelSequence();

    if (primaryChannel) {
      primaryConfigs = {
        showPrimaryButton: true,
        showPrimaryClockDiv: true,
        primaryChannel: primaryChannel,
        primaryButtonColor: 'nocolor',
        primaryClockDivId: this.getChannelClockDivId(primaryChannel),
        primaryButtonText: this.getButtonLabel(primaryChannel),
        primaryAction: () => actionFn(primaryChannel)
      }
    }
    
    if (secondaryChannel) {
      secondaryConfigs = {
        showSecondaryButton: true,
        showSecondaryClockDiv: true,
        secondaryChannel: secondaryChannel,
        secondaryColor: 'nocolor',
        secondaryClockDivId: this.getChannelClockDivId(secondaryChannel),
        secondaryButtonText: this.getButtonLabel(secondaryChannel),
        secondaryAction: () => actionFn(secondaryChannel)
      }
    }
    
    if (tertiaryChannel) {
      tertiaryConfigs = {
        showTertiaryButton: true,
        showTertiaryClockDiv: true,
        tertiaryChannel: tertiaryChannel,
        tertiaryColor: 'nocolor',
        tertiaryClockDivId: this.getChannelClockDivId(tertiaryChannel),
        tertiaryButtonText: this.getButtonLabel(tertiaryChannel),
        tertiaryAction: () => actionFn(tertiaryChannel)
      }
    }
    
    return {
      primaryConfigs,
      secondaryConfigs,
      tertiaryConfigs
    }
  }

  getChannelCooldown(channel: string, otpPayload) {

    if (otpPayload
      && otpPayload.Channel
      && otpPayload.Channel.toLowerCase() === channel.toLowerCase()) {

        if (otpPayload.CooldownRemaining) {
          return otpPayload.CooldownRemaining
        }
    }

    return 0;
  }

  getEventTypeForChannel(channel: string) {
    switch(channel) {
      case EVENTS_VALUES.SMS: return EVENTS_VALUES.TRY_PHONE;
      case EVENTS_VALUES.EMAIL: return EVENTS_VALUES.TRY_EMAIL;
      case EVENTS_VALUES.VOICE: return EVENTS_VALUES.CALL_ME_INSTEAD;
      case EVENTS_VALUES.WHATSAPP: return EVENTS_VALUES.WHATSAPP;
    }
  }

  getDeliveryType(channel: string): number {
    switch(channel) {
      case EVENTS_VALUES.EMAIL: return DELIVERY_TYPE.EMAIL;
      case EVENTS_VALUES.VOICE: return DELIVERY_TYPE.VOICE;
      case EVENTS_VALUES.WHATSAPP: return DELIVERY_TYPE.WHATSAPP;
      default: return DELIVERY_TYPE.SMS;
    }
  }

  getVerificationMethod(deliveryType: number): string {
    switch(deliveryType) {
      case DELIVERY_TYPE.SMS: return SegmentLocationNames.SmsPhoneVerification;
      case DELIVERY_TYPE.EMAIL: return SegmentLocationNames.EmailVerification;
      case DELIVERY_TYPE.VOICE: return SegmentLocationNames.VoicePhoneVerfication;
      case DELIVERY_TYPE.WHATSAPP: return SegmentLocationNames.WhatsAppPhoneVerification;
      default: return 'N/A';
    }
  }

  getMaskedSubtitle(key: string, maskedIdentifier: string): string {
    try {
      return this.translate.instant(key, { maskedIdentifier }).replace('{maskedIdentifier}', maskedIdentifier || '');
    } catch (e) {
      LoggerProvider.info(this, 'getMaskedSubtitle failed, falling back to identifier: ' + (e && e.message ? e.message : e));
      return maskedIdentifier || '';
    }
  }

  maskPhoneForDisplay(national: string, prefix: string): string {
    const digits = (national || '').replace(/\D/g, '');
    if (!digits) return '';
    const cc = (prefix || '').replace(/\D/g, '');
    return (cc ? '+' + cc + ' ' : '') + '*** *** ' + digits.slice(-3);
  }

  isWhatsAppOtpEnabled(): boolean {
    return UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.WhatsAppOtpEnabled) === true;
  }

  isWhatsAppEligible(flow: string, flagEnabled: boolean): boolean {
    return flagEnabled === true && WHATSAPP_ELIGIBLE_FLOWS.indexOf(flow) !== -1;
  }

  private channelOption(channel: string) {
    switch (channel) {
      case EVENTS_VALUES.WHATSAPP:
        return { value: EVENTS_VALUES.WHATSAPP, label: this.translate.instant('page.otp.channelSelector.optionWhatsapp'), iconSrc: 'assets/icons/otp-channel/whatsapp.svg' };
      case EVENTS_VALUES.VOICE:
        return { value: EVENTS_VALUES.VOICE, label: this.translate.instant('page.otp.channelSelector.optionCall'), iconSrc: 'assets/icons/otp-channel/call.svg' };
      default:
        return { value: EVENTS_VALUES.SMS, label: this.translate.instant('page.otp.channelSelector.optionSms'), iconSrc: 'assets/icons/otp-channel/text.svg' };
    }
  }

  private buildChannelSelectorParams(maskedIdentifier: string, options: any[], selectedValue: string, onSelect: (channel: string) => void, onUseAnotherNumber: () => void, analytics?: ChannelSelectorAnalytics) {
    return {
      title: this.translate.instant('page.otp.channelSelector.title'),
      subtitle: this.getMaskedSubtitle('page.otp.channelSelector.subtitle', maskedIdentifier),
      options,
      selectedValue,
      continueText: this.translate.instant('page.otp.channelSelector.continue'),
      hyperlinkText: this.translate.instant('page.otp.channelSelector.useAnotherNumber'),
      onShown: () => this.fireChannelSelectorEvent(AnalyticsEventType.OtpChannelSelectorShown, analytics),
      onContinue: (value: string) => { this.fireChannelSelectedEvent(value, analytics); onSelect(value); },
      onHyperlink: onUseAnotherNumber,
      onDismissed: () => this.fireChannelSelectorEvent(AnalyticsEventType.OtpChannelSelectorDismissed, analytics),
    };
  }

  getChannelSelectorParams(displayNumber: string, onSelect: (channel: string) => void, onUseAnotherNumber: () => void, analytics?: ChannelSelectorAnalytics) {
    const options = [this.channelOption(EVENTS_VALUES.SMS)];
    if (this.isWhatsAppOtpEnabled()) {
      options.push(this.channelOption(EVENTS_VALUES.WHATSAPP));
    }
    options.push(this.channelOption(EVENTS_VALUES.VOICE));
    return this.buildChannelSelectorParams(displayNumber, options, EVENTS_VALUES.SMS, onSelect, onUseAnotherNumber, analytics);
  }

  private emitOtpEvent(eventName: AnalyticsEventType, analyticsData: any) {
    try {
      this.segment.trackEvent({ eventName, analyticsData });
    } catch (e) {
      LoggerProvider.info(this, 'OTP channel event failed: ' + (e && e.message ? e.message : e));
    }
  }

  private fireChannelSelectorEvent(eventName: AnalyticsEventType, analytics?: ChannelSelectorAnalytics) {
    if (!analytics) return;
    this.emitOtpEvent(eventName, { location: this.toSegmentLocation(analytics.flow), country: analytics.country });
  }

  private fireChannelSelectedEvent(value: string, analytics?: ChannelSelectorAnalytics) {
    if (!analytics) return;
    this.emitOtpEvent(AnalyticsEventType.OtpChannelSelected, { location: this.toSegmentLocation(analytics.flow), verificationMethod: this.getVerificationMethod(this.getDeliveryType(value)), country: analytics.country, source: 'selector' });
  }

  fireHelpSheetChannelSelected(channel: string, flow: string, country: string) {
    if (!flow) return;
    this.emitOtpEvent(AnalyticsEventType.OtpChannelSelected, { location: this.toSegmentLocation(flow), verificationMethod: this.getVerificationMethod(this.getDeliveryType(channel)), country, source: 'help_sheet' });
  }

  private toSegmentLocation(flow: string): string {
    return flow === WHATSAPP_FLOW.LOGIN ? SegmentLocationNames.Login : SegmentLocationNames.Registration;
  }

  getLoginChannelSelectorParams(maskedDestination: string, availableChannels: Array<{ Type: string; MaskedDestination?: string }>, onSelect: (channel: string) => void, onUseAnotherNumber: () => void, analytics?: ChannelSelectorAnalytics) {
    const channels = Array.isArray(availableChannels) ? availableChannels : [];
    const options = [];
    let hasSms = false;
    let hasWhatsApp = false;
    channels.forEach((channel) => {
      const type = (channel && channel.Type) ? channel.Type.toLowerCase() : '';
      if (type === BE_CHANNEL_TYPE.SMS && !hasSms) {
        options.push(this.channelOption(EVENTS_VALUES.SMS));
        hasSms = true;
      } else if (type === BE_CHANNEL_TYPE.WHATSAPP && !hasWhatsApp) {
        options.push(this.channelOption(EVENTS_VALUES.WHATSAPP));
        hasWhatsApp = true;
      }
    });
    const selectedValue = (!hasSms && options.length) ? options[0].value : EVENTS_VALUES.SMS;
    return this.buildChannelSelectorParams(maskedDestination, options, selectedValue, onSelect, onUseAnotherNumber, analytics);
  }

  isCooldownEnable(otpTimerResponse) {
    // check if the otpTimerResponse.Result.RetVal = 250 then cooldown enabled.
    // OR check for otpTimerResponse.Value.CooldownEnable
    return !!(
      otpTimerResponse && (
        (otpTimerResponse.Result && otpTimerResponse.Result.RetVal === OTP_RESULT.COOLDOWN_STATUS)
        || (otpTimerResponse.Value && otpTimerResponse.Value.CooldownEnable === true)
      )
    );
  }
    
  isChannelExistInResponse(channel) {
    const lowerChannel = channel ? channel.toLowerCase() : '';
    return BASE_ORDER.some(item => item.toLowerCase() === lowerChannel) 
  }

  getChannelFromResponse(response) {
    const channel = response.Channel;

    if (this.isChannelExistInResponse(channel)) {
      switch(channel.toLowerCase()) {
        case 'sms': return EVENTS_VALUES.SMS;
        case 'email': return EVENTS_VALUES.EMAIL;
        case 'voice': return EVENTS_VALUES.VOICE;
        case 'whatsapp': return EVENTS_VALUES.WHATSAPP;
      }
    }
  }

  getCooldownPropsFromHeader(headers) {
    const channel = this.http.getHeadersValue(headers, 'channel');
    const coolDownEnable = this.http.getHeadersValue(headers, 'cooldownenable') === 'true';
    const coolDownRemaining = this.http.getHeadersValue(headers, 'cooldownremaining')
    
    if (this.isChannelExistInResponse(channel)
      && coolDownEnable
      && coolDownRemaining > 0
    ) {
      return {
        'Value': {
          'Channel': channel,
          'CooldownEnable': coolDownEnable,
          'CooldownRemaining': coolDownRemaining
        }
      }
    }

    return null;
  }
  
  checkStatusAndNavigateToOtp(otpResponseStatusCode: number) {
    const includedStatus = [
      OTP_RESULT.SUCCESS,
      OTP_RESULT.INTERNAL_BETWEEN_ATTEMPTS,
      OTP_RESULT.COOLDOWN_STATUS
    ];

    return includedStatus.includes(otpResponseStatusCode);
  }

  isTwilioUnavailable(response): boolean {
    return (
      response &&
      response.Result &&
      response.Result.RetVal === OTP_RESULT.TWILIO_SERVICE_UNAVAILABLE_STATUS
    );
  }

  handleTwilioUnavailable() {
    this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
      subtitle: this.translate.instant('Page.otpFailed.otpFailedTwillioIssue'),
      primaryButtonText: this.translate.instant('global.gotItButton')
    });
    LoggerProvider.info(this, 'Twilio service is currently unavailable.');
  }

  isOtpSendBlocked(response): boolean {
    return (
      response &&
      response.Result &&
      response.Result.RetVal === OTP_RESULT.OTP_SEND_BLOCKED
    );
  }

  handleOtpSendBlocked() {
    this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
      subtitle: this.translate.instant('global.mFAContainer.codeSendFailedMessage'),
      primaryButtonText: this.translate.instant('global.gotItButton')
    });
    this.loadingProvider.hide();
    LoggerProvider.info(this, 'OTP send was blocked by the provider.');
  }

  focusInput(input: any, keyboard?: any, ev?: any) {
    if (this.inputFocusTimeout) {
      window.clearTimeout(this.inputFocusTimeout);
    }

    this.inputFocusTimeout = window.setTimeout(() => {
      if (input && input.nativeElement) {
        input.nativeElement.focus();
        if (keyboard) {
          keyboard.keyboardType = "tel";
        }
      }
    }, 200);

    if (ev && ev.preventDefault) {
      ev.preventDefault();
    }
  }

  clearInputFocusTimeout() {
    if (this.inputFocusTimeout) {
      window.clearTimeout(this.inputFocusTimeout);
      this.inputFocusTimeout = null;
    }
  }

  setupResumeSubscription(input: any, keyboard?: any) {
    if (this.platform.is('cordova') && !this.resumeSubscription) {
      this.resumeSubscription = this.platform.resume.subscribe(() => {
        this.focusInput(input, keyboard);
      });
    }
  }

  cleanupResumeSubscription() {
    if (this.resumeSubscription) {
      this.resumeSubscription.unsubscribe();
      this.resumeSubscription = null;
    }
  }

  isOtpSessionExpired(response: any): boolean {
    if (!response || !response.Result) return false;
    if (response.Result.RetVal !== OTP_RESULT.ERROR_UPDATING) return false;
    const retMsg = (response.Result.RetMsg || '').toLowerCase().replace(/\s/g, '');
    return retMsg.startsWith('otpnotfound');
  }

  getOtpSessionExpiredMessage(response: any): string {
    if (!response || !response.Result) return '';
    const retMsg = (response.Result.RetMsg || '').toLowerCase().replace(/\s/g, '');
    const key = OTP_SESSION_EXPIRED_KEY_MAP[retMsg];
    return this.translate.instant(key || 'page.eWalletPage.walletErrorContent');
  }

  showOtpSessionExpiredModal(response: any, primaryAction: () => void) {
    this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, {
      title: this.translate.instant('global.errorText'),
      subtitle: this.getOtpSessionExpiredMessage(response),
      primaryButtonText: this.translate.instant('global.okButton'),
      primaryAction
    }, null);
  }

  classifyMobileVerifyResponse(res: any): MobileVerifyClassification {
    const otpResult = (res && res.Result) ? res.Result.RetVal : null;
    const value = (res && res.Value) ? res.Value : null;
    
    if (value && (
      otpResult == OTP_REGISTRATION_RET_VAL.OK
      || otpResult == OTP_REGISTRATION_RET_VAL.INTERVAL_ATTEMPTS
      || otpResult == OTP_RESULT.INTERNAL_BETWEEN_ATTEMPTS
      || otpResult == OTP_RESULT.COOLDOWN_STATUS
    )) {
      return { outcome: MobileVerifyOutcome.OpenOtp, otpResult, value };
    }

    if (res && res.Result && (
      res.Result.RetVal == OTP_REGISTRATION_RET_VAL.BLOCKED_BY_OTP_FAILED_ATTEMPS
      || res.Result.RetVal == OTP_REGISTRATION_RET_VAL.BLOCKED_BY_TWILIO
    )) {
      return { outcome: MobileVerifyOutcome.Blocked, otpResult, value };
    }

    if (this.isTwilioUnavailable(res)) {
      return { outcome: MobileVerifyOutcome.TwilioUnavailable, otpResult, value };
    }

    if (this.isOtpSendBlocked(res)) {
      return { outcome: MobileVerifyOutcome.SendBlocked, otpResult, value };
    }

    if (otpResult == OTP_RESULT.SEND_MFA_MAX_ATTEMPTS.GENERAL) {
      return { outcome: MobileVerifyOutcome.MaxAttempts, otpResult, value };
    }

    if (otpResult == OTP_RESULT.INVALID) {
      return { outcome: MobileVerifyOutcome.Invalid, otpResult, value };
    }

    return { outcome: MobileVerifyOutcome.Failed, otpResult, value };
  }
}



// WEBPACK FOOTER //
// ./src/providers/otp-timer/otp-timer.ts